/**
 * RunSection — the Run-summary section for the workflow detail drawer, in ora
 * and in the plugin.
 *
 * This is the ONE Run section. It used to have a sibling, `RunChip`, and under
 * the running-drawer flag both rendered inside More information — the chip via
 * `extraBlocks` and this section via the grouped order — so the pane showed two
 * "Run" accordions with overlapping-but-not-equal fields. The chip carried
 * Version and a collapsed-header summary this section lacked; this section
 * carried Current step and Progress the chip lacked, and computed Duration for
 * an in-flight run where the chip showed none. Both sets live here now and
 * RunChip is gone.
 *
 * Calm-UI behaviour: the section ALWAYS renders its full field set (Triggered
 * by / Started / Ended / Duration / Current step / Progress / Version).
 * Inapplicable fields render their em-dash placeholder via InstanceField's
 * built-in fallback. Body height therefore never changes as the workflow
 * transitions from running → paused_hitl → terminal, eliminating the jitter
 * that conditional-field rendering caused.
 *
 * Presentation-only: every value arrives pre-formatted. Callers own the
 * formatting so the two apps can source the same field from different places
 * (ora from its instance row, the plugin from the push snapshot) without this
 * component learning about either.
 */

import { DrawerSection } from "../DrawerSection";
import { InstanceField } from "../../components/InstanceField";

export interface RunSectionProps {
  triggeredByName: string | null | undefined;
  /** Absolute start, e.g. "09/02/2026 8:27 pm". */
  startedAtFormatted: string | null | undefined;
  endedAtFormatted: string | null | undefined;
  duration: string | null | undefined;
  currentStep: string | null | undefined;
  progress: string | null | undefined;
  /** Workflow version, e.g. "v2" — from `displayWorkflowVersion`. */
  workflowVersion?: string | null;
  /**
   * Short start time, e.g. "8:27 pm", for the collapsed header summary
   * ("Started 8:27 pm · 8m 56s"). Separate from `startedAtFormatted` because
   * the header wants the time alone while the row wants the full date — the
   * same split RunChip made with `formatTime` vs the caller's `formatAbsolute`.
   * Omit to render no header summary.
   */
  startedAtShort?: string | null;
  defaultExpanded?: boolean;
  persistKey?: string;
}

export function RunSection({
  triggeredByName,
  startedAtFormatted,
  endedAtFormatted,
  duration,
  currentStep,
  progress,
  workflowVersion,
  startedAtShort,
  defaultExpanded,
  persistKey,
}: RunSectionProps) {
  const rightAdornment =
    startedAtShort || duration ? (
      <span className="text-xs text-primary-gray-light-500 truncate flex items-center gap-1">
        {startedAtShort && (
          <>
            <span>Started</span>
            <span className="font-semibold text-primary-gray-light-900">
              {startedAtShort}
            </span>
          </>
        )}
        {startedAtShort && duration && <span> · </span>}
        {duration && (
          <span className="font-semibold text-primary-gray-light-900">
            {duration}
          </span>
        )}
      </span>
    ) : undefined;

  return (
    <DrawerSection
      title="Run"
      defaultExpanded={defaultExpanded}
      persistKey={persistKey}
      rightAdornment={rightAdornment}
    >
      <InstanceField label="Triggered by" value={triggeredByName} />
      <InstanceField label="Started" value={startedAtFormatted} />
      <InstanceField label="Ended" value={endedAtFormatted} />
      <InstanceField label="Duration" value={duration} />
      <InstanceField label="Current step" value={currentStep} />
      <InstanceField label="Progress" value={progress} />
      <InstanceField label="Version" value={workflowVersion} />
    </DrawerSection>
  );
}

export default RunSection;
